import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import {
  listParameterClasses,
  listTemplates,
  PLATFORM_CLASS,
  type QuestionnaireParameterClassRow,
  type QuestionnaireTemplate,
} from "@web/questionnaires/questionnaireParametersApi";
import { ParameterSections, fromClassRow } from "@web/questionnaires/ParameterSections";
import { PAGE_TITLE_ID } from "@web/app/pageTitle";

// KM-E9 / US-E9.2b · PLATFORM SETTINGS (spec luku 7.3, class "Alustataso") — the platform operator's page.
//
// Two parameters live at this level, and both are stated rather than offered: the instrument template /
// FACTOR LIBRARY, which ships with the software and is identical for every tenant, and a platform-wide
// raise of k above five. Neither has a write surface, and that is the design: a k raise applied to every
// customer at once is a deployment decision, not a request a form should be able to make. So this page
// says who owns each value, when it may move, and what it is — which is exactly what a settings page for
// values nobody may type is for.
//
// It is a SEPARATE page from the tenant settings on purpose (US-E9.2b): conflating the two is how a tenant
// admin ends up looking at a platform switch. The taxonomy itself is the server's — the rows come from GET
// /api/questionnaires/parameters and are filtered by the CLASS the API put them in, never by a list here.
//
// A note on authorization, because this page's audience is exactly the question it raises. The parameter
// catalogue and the template library are read behind the questionnaire-management role OR PLATFORM_ADMIN
// (US-E9.2c, 3.8.2026): both are properties of the software VERSION, identical for every tenant, so a
// platform operator reading them learns nothing about anyone — and they describe the very parameters luku
// 7.3 assigns to that operator. FR-Q.34 is untouched: the platform role still holds no tenant authority,
// and the per-questionnaire read behind /admin/questionnaires/:id/settings still refuses it.
//
// The refusal branches below therefore no longer fire for a platform session. They stay because they are
// the honest rendering of a 403 from anyone else the route gate lets through: a page that quietly showed
// nothing would read as "there is nothing to configure", which is a different and false statement.

type Load =
  | { phase: "loading" }
  | { phase: "forbidden"; detail: string | null }
  | { phase: "error" }
  | { phase: "ready"; classes: QuestionnaireParameterClassRow[] };

/** Coerce a contract numeric (openapi-typescript widens to `number | string`) for display. */
function num(value: number | string): string {
  return String(value);
}

export function PlatformSettingsPage() {
  const { t, i18n } = useTranslation();
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [templates, setTemplates] = useState<QuestionnaireTemplate[] | null>(null);
  const [templatesDenied, setTemplatesDenied] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listParameterClasses(controller.signal)
      .then((classes) => {
        if (controller.signal.aborted) return;
        setLoad({ phase: "ready", classes });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        if (err instanceof ApiError && err.status === 403) {
          setLoad({ phase: "forbidden", detail: err.problem?.detail ?? null });
        } else {
          setLoad({ phase: "error" });
        }
      });
    return () => controller.abort();
  }, []);

  // The factor library itself (US-E5.1): what the platform-level parameter actually contains.
  useEffect(() => {
    const controller = new AbortController();
    listTemplates(controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setTemplates(rows);
        setTemplatesDenied(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        setTemplates(null);
        setTemplatesDenied(
          err instanceof ApiError
            ? (err.problem?.detail ?? t("questionnaires.settings.libraryUnavailable"))
            : t("questionnaires.settings.libraryUnavailable"),
        );
      });
    return () => controller.abort();
  }, [t]);

  const localized = (text: { fi?: string | null; en?: string | null } | null | undefined): string => {
    if (!text) return "";
    const preferred = i18n.language?.startsWith("en") ? text.en : text.fi;
    return preferred ?? text.fi ?? text.en ?? "";
  };

  return (
    <section className="questionnaire-settings" aria-labelledby={PAGE_TITLE_ID}>
      <header className="questionnaire-report__head">
        <p className="questionnaire-report__subtitle">
          {t("questionnaires.settings.platformSubtitle")}
        </p>
      </header>

      <p className="questionnaire-report__hint">{t("questionnaires.settings.platformHint")}</p>

      {load.phase === "loading" && (
        <p className="empty-state" role="status" aria-live="polite" data-testid="settings-loading">
          {t("questionnaires.settings.loading")}
        </p>
      )}

      {load.phase === "error" && (
        <p className="banner banner--error" role="alert" data-testid="settings-error">
          {t("questionnaires.settings.loadError")}
        </p>
      )}

      {/* The API refused this caller. FR-Q.34 is why that can happen to a platform admin, and the honest
          rendering is the server's own refusal — not a page that quietly shows nothing. */}
      {load.phase === "forbidden" && (
        <div className="banner banner--warn" role="status" data-testid="settings-forbidden">
          <p>{t("questionnaires.settings.platformForbidden")}</p>
          {load.detail && <p className="questionnaire-report__base">{load.detail}</p>}
        </div>
      )}

      {load.phase === "ready" && (
        <ParameterSections
          parameters={load.classes.map(fromClassRow)}
          // Only the class the platform operator owns. The rest of the table is the tenant's page.
          filter={(p) => p.class === PLATFORM_CLASS}
        />
      )}

      {/* The factor library as it actually ships: one row per instrument template, with the F and m the
          sizing rule is computed from. Read-only by nature — the library is code, not tenant data. */}
      <section className="parameter-class" aria-labelledby="platform-library-title">
        <h2 id="platform-library-title">{t("questionnaires.settings.libraryTitle")}</h2>
        <p className="questionnaire-report__hint">{t("questionnaires.settings.libraryHint")}</p>

        {templatesDenied && (
          <p className="banner banner--warn" role="status" data-testid="settings-library-denied">
            {templatesDenied}
          </p>
        )}

        {templates && templates.length === 0 && (
          <p className="empty-state" role="status" data-testid="settings-library-empty">
            {t("questionnaires.settings.libraryEmpty")}
          </p>
        )}

        {templates && templates.length > 0 && (
          <div className="questionnaire-report__table-scroll">
            <table className="table questionnaire-report__table" data-testid="settings-library">
              <thead>
                <tr>
                  <th scope="col">{t("questionnaires.settings.library.template")}</th>
                  <th scope="col">{t("questionnaires.settings.library.version")}</th>
                  <th scope="col">{t("questionnaires.settings.library.factors")}</th>
                  <th scope="col">{t("questionnaires.settings.library.selections")}</th>
                  <th scope="col">{t("questionnaires.settings.library.questions")}</th>
                  <th scope="col">{t("questionnaires.settings.library.themes")}</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.key} data-testid="settings-library-row" data-template={template.key}>
                    <th scope="row">
                      {localized(template.name)}
                      <span className="questionnaire-report__base">{template.key}</span>
                    </th>
                    <td data-testid="library-version">{num(template.version)}</td>
                    <td className="survey-admin__num" data-testid="library-factors">
                      {num(template.factorCount)}
                    </td>
                    <td className="survey-admin__num" data-testid="library-selections">
                      {num(template.selectCount)}
                    </td>
                    <td className="survey-admin__num" data-testid="library-questions">
                      {num(template.questionCount)}
                    </td>
                    <td data-testid="library-themes">{template.themes.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
